import * as React from "react"
import { useEvent } from "react-use"
import { Interval, type IntervalOrigin, type Period } from "@digits-graphql/frontend/graphql-bearer"
import { SvgArrowNarrowLeft } from "@digits-shared/components/SVGIcons/line/ArrowNarrowLeft.svg"
import { Keys } from "@digits-shared/components/UI/Elements/Keys"
import { DigitsButton } from "@digits-shared/DesignSystem/Button"
import zIndexes from "@digits-shared/DesignSystem/zIndexes"
import { type TimeRange } from "@digits-shared/helpers/dateTimeHelper"
import useForwardedRef from "@digits-shared/hooks/useForwardedRef"
import { useOnBodyClick } from "@digits-shared/hooks/useOnBodyClick"
import colors from "@digits-shared/themes/colors"
import fonts from "@digits-shared/themes/typography"
import styled from "styled-components"
import {
  DATE_RANGE_MAX_DAYS,
  type DateRangeIntervalOptions,
} from "src/shared/components/DateRangeSelector/dateRangeConstants"
import {
  DateRangeContextProvider,
  useDateRangeContext,
} from "src/shared/components/DateRangeSelector/DateRangeContext"
import {
  DateRangePortal,
  useDateRangePortalState,
} from "src/shared/components/DateRangeSelector/DateRangePortal"
import { DateRangePresets } from "src/shared/components/DateRangeSelector/DateRangePresets"
import {
  IntervalRangeSelector,
  IntervalYearSelector,
} from "src/shared/components/DateRangeSelector/DateRangeSelectors"
import { IntervalSelector } from "src/shared/components/DateRangeSelector/IntervalSelector"
import customKeyframes from "src/shared/config/customKeyframes"

// Lazy loading DayRangePicker to avoid pulling in the react-day-picker and other assets during the app launch
const DayRangePicker = React.lazy(() =>
  import("src/shared/components/DateRangeSelector/DayRangePicker").then((mod) => ({
    default: mod.DayRangePicker,
  }))
)

const DateRangeSelectorContainer = styled.div`
  .mobile-only & {
    max-width: 100vw;
  }
  border-radius: 8px;
  background: ${colors.white};
  box-shadow: 0 0 24px 0 ${colors.secondary50};

  position: absolute;
  z-index: ${zIndexes.pageHeaderDropdown};
  display: flex;
  max-height: 490px;

  font-size: 14px;
  font-weight: ${fonts.weight.medium};
  line-height: normal;
  text-transform: capitalize;
  text-align: left;

  opacity: 0;
  animation: ${customKeyframes.fadeIn} 250ms 200ms forwards;
`

const RangeSelectorContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 12px;
  border-radius: 0 24px 24px 0;

  padding: 23px;
  .mobile-only & {
    width: 100vw;
  }
  .mobile & {
    padding: 22px 14px;
  }
`

const RangeContainer = styled.div`
  display: flex;
  flex: 1;
  overflow: hidden;
  touch-action: none;

  gap: 38px;
  .mobile-only & {
    gap: unset;
    justify-content: space-between;
  }
`

const Footer = styled.div`
  display: flex;
  gap: 12px;
  justify-content: space-between;
`

const RangeError = styled.div`
  display: flex;
  text-transform: none;
  align-items: center;
  color: ${colors.error};
  font-size: 14px;
`

const Buttons = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  flex-grow: 1;

  ${DigitsButton} {
    min-width: 75px;
  }
`

interface DateRangeProps {
  positionRef: React.RefObject<HTMLElement | null>
  className?: string
  defaultValue?: Period | IntervalOrigin
  onChange?: (timeRange: TimeRange) => void
  onTimeRange: (timeRange: TimeRange) => void
  onCancel: () => void
  intervals?: DateRangeIntervalOptions
}

export const DateRangeSelector: React.FC<DateRangeProps> = ({
  positionRef,
  defaultValue,
  onChange,
  onTimeRange,
  onCancel,
  className,
  intervals,
}) => {
  const { selectorRef, position } = useDateRangePortalState(positionRef)
  return (
    <DateRangePortal position={position}>
      <DateRangeContextProvider
        defaultValue={defaultValue}
        onTimeRange={onTimeRange}
        onCancel={onCancel}
        intervals={intervals}
      >
        <DateRange ref={selectorRef} className={className} onChange={onChange} />
      </DateRangeContextProvider>
    </DateRangePortal>
  )
}

const DateRange = React.forwardRef<
  HTMLDivElement,
  { className?: string; onChange?: (timeRange: TimeRange) => void }
>(({ className, onChange }, ref) => {
  const forwardRef = useForwardedRef(ref)
  const {
    dateRangeState: { timeRange, custom },
    onCancelClick,
  } = useDateRangeContext()

  // notify caller of each time change
  React.useEffect(() => {
    onChange?.(timeRange)
  }, [timeRange, timeRange.startedAt, timeRange.endedAt, timeRange.interval, onChange])

  const handleKeyDown = React.useCallback(
    (event: KeyboardEvent) => {
      if (event.key === Keys.Escape) onCancelClick()
    },
    [onCancelClick]
  )
  useEvent("keydown", handleKeyDown, document, { capture: false })
  useOnBodyClick(forwardRef, onCancelClick)

  return (
    <DateRangeSelectorContainer ref={forwardRef} className={className} role="dialog">
      {!custom && <DateRangePresets onChange={onChange} />}
      {custom && <RangeSelector />}
    </DateRangeSelectorContainer>
  )
})

const RangeSelector: React.FC = () => {
  const { onSetClick, onCancelClick, dateRangeState, dateRangeDispatch } = useDateRangeContext()
  const isInvalid = dateRangeState.timeRange.interval === Interval.Day && !dateRangeState.valid

  return (
    <RangeSelectorContainer>
      <DigitsButton
        size="small"
        $variant="ghost-dark"
        onClick={() => dateRangeDispatch({ type: "TOGGLE_CUSTOM", custom: false })}
      >
        <SvgArrowNarrowLeft />
        Back to presets
      </DigitsButton>
      <IntervalSelector />
      <RangeContainer>
        <RangeOptions />
      </RangeContainer>
      <Footer>
        {isInvalid && <RangeError>Please select {DATE_RANGE_MAX_DAYS} days or less</RangeError>}
        <Buttons>
          <DigitsButton $variant="secondary-dark" size="medium" onClick={onCancelClick}>
            Cancel
          </DigitsButton>
          <DigitsButton onClick={onSetClick} size="medium" disabled={isInvalid}>
            Set
          </DigitsButton>
        </Buttons>
      </Footer>
    </RangeSelectorContainer>
  )
}

const RangeOptions: React.FC = () => {
  const {
    dateRangeState: {
      timeRange: { interval },
    },
  } = useDateRangeContext()

  switch (interval) {
    case Interval.Day:
      return (
        <React.Suspense>
          <DayRangePicker />
        </React.Suspense>
      )

    case Interval.Year:
      return <IntervalYearSelector />

    default:
      return <IntervalRangeSelector />
  }
}
